import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { activityList, activityCsv, downloadBlob, filenameFromResponse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Activity as ActivityIcon, FileDown, Search, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const ALL_ACTIONS = {
  login: "Anmeldung",
  receipt_create: "Beleg erstellt",
  receipt_update: "Beleg geändert",
  receipt_delete: "Beleg gelöscht",
  receipt_export: "Export",
  invite_sent: "Einladung versendet",
  invite_accepted: "Einladung angenommen",
  account_opened: "Konto geöffnet",
  permission_changed: "Berechtigung geändert",
  decoupled: "Entkopplung",
};
const RECEIPT_ACTIONS = ["receipt_create", "receipt_update", "receipt_delete", "receipt_export"];

const fmtTs = (ts) => {
  try {
    return new Date(ts).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return ts;
  }
};

export default function Activity() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const actionOptions = useMemo(() => {
    const keys = isAdmin ? Object.keys(ALL_ACTIONS) : RECEIPT_ACTIONS;
    return keys.map((k) => ({ value: k, label: ALL_ACTIONS[k] }));
  }, [isAdmin]);

  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (action && action !== "all") p.action = action;
    if (q.trim()) p.q = q.trim();
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [action, q, from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await activityList(params()));
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Protokoll konnte nicht geladen werden.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setAction("all");
    setQ("");
    setFrom("");
    setTo("");
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await activityCsv(params());
      downloadBlob(res.data, filenameFromResponse(res, "BeSca_Aktivitaet.csv"));
    } catch {
      toast.error("Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex items-center gap-2">
        <ActivityIcon className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Aktivität</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin
              ? "Alle Aktivitäten aller Konten – durchsuchbar und filterbar."
              : "Beleg-Aktivitäten Ihres Bereichs (erstellt, geändert, gelöscht, exportiert)."}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-xs">Aktion</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="bg-white" data-testid="activity-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="activity-action-all">Alle Aktionen</SelectItem>
              {actionOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`activity-action-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs" htmlFor="act-search">Suche (Name / Firma / E-Mail)</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="act-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="suchen…"
              className="bg-white pl-8"
              data-testid="activity-search"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs" htmlFor="act-from">Von</Label>
          <Input id="act-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="bg-white" data-testid="activity-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs" htmlFor="act-to">Bis</Label>
          <Input id="act-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="bg-white" data-testid="activity-to" />
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" className="border border-border gap-2" onClick={reset}
            data-testid="activity-reset">
            <RotateCcw className="h-4 w-4" /> Zurücksetzen
          </Button>
        </div>
      </div>

      {/* Liste */}
      <div className="border border-border bg-white">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="font-medium">
            Einträge <span className="text-sm text-muted-foreground">({rows.length})</span>
          </div>
          <Button
            size="sm"
            className="gap-2"
            onClick={doExport}
            disabled={exporting || rows.length === 0}
            data-testid="activity-export-csv"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            CSV exportieren
          </Button>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Lädt…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground" data-testid="activity-empty">
            Keine Aktivitäten im gewählten Filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-4 py-2 whitespace-nowrap">Zeitpunkt</th>
                  <th className="text-left font-medium px-4 py-2 whitespace-nowrap">Aktion</th>
                  <th className="text-left font-medium px-4 py-2">Akteur</th>
                  {isAdmin && <th className="text-left font-medium px-4 py-2">Kontotyp</th>}
                  <th className="text-left font-medium px-4 py-2">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} data-testid="activity-row" className="align-top">
                    <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{fmtTs(r.ts)}</td>
                    <td className="px-4 py-2 whitespace-nowrap font-medium">{r.action_label}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.actor_name || r.actor_company || "—"}</div>
                      <div className="text-xs text-muted-foreground">{r.actor_email}</div>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                        {r.actor_account_type}
                      </td>
                    )}
                    <td className="px-4 py-2 text-muted-foreground">{r.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
